"use client";

import { useRef, useState, useTransition } from "react";
import { ArrowLeft, ArrowRight, KeyRound, Mail } from "lucide-react";
import { requestCode, signInWithPassword, verifyCode } from "./actions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { createBrowserSupabaseClient } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";

const ERROR_MESSAGES: Record<string, string> = {
  invalid_input: "Digite um email válido.",
  not_found: "Email não cadastrado.",
  send_failed: "Não consegui enviar o código. Tenta de novo.",
  invalid_code: "Código inválido ou expirado. Peça um novo.",
  invalid_password: "Email ou senha inválidos.",
  oauth_start_failed: "Não consegui iniciar login com Google. Tenta de novo.",
  oauth_missing_code: "Login com Google falhou (sem código). Tenta de novo.",
  oauth_exchange_failed: "Login com Google falhou na troca de token.",
  nao_autorizado: "Esse Google não tem acesso ao Skynet. Pede pro admin cadastrar.",
};

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.1V7.06H2.18A10.99 10.99 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}

export function LoginForm({ initialError }: { initialError?: string } = {}) {
  const [step, setStep] = useState<"email" | "code" | "password">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | undefined>(initialError);
  const [pending, startTransition] = useTransition();

  function handleGoogle() {
    setError(undefined);
    startTransition(async () => {
      const supabase = createBrowserSupabaseClient();
      const redirectTo = `${window.location.origin}/auth/callback?next=${encodeURIComponent("/dashboard")}`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo,
          queryParams: { access_type: "offline", prompt: "consent" },
        },
      });

      if (error) {
        setError("oauth_start_failed");
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    });
  }

  function errorMsg() {
    if (!error) return undefined;
    return ERROR_MESSAGES[error] ?? "Algo deu errado. Tenta de novo.";
  }

  function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    startTransition(async () => {
      const result = await requestCode(email);
      if (result?.ok) {
        setStep("code");
      } else {
        setError(result?.error ?? "send_failed");
      }
    });
  }

  function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    startTransition(async () => {
      const result = await verifyCode(email, code);
      if (!result?.ok) {
        setError(result?.error ?? "invalid_code");
      }
      // Em caso de sucesso, a action faz redirect no servidor
    });
  }

  function handlePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    startTransition(async () => {
      const result = await signInWithPassword(email, password);
      if (!result?.ok) {
        setError(result?.error ?? "invalid_password");
      }
    });
  }

  if (step === "email") {
    return (
      <div className="flex flex-col gap-4">
        <Button
          type="button"
          variant="secondary"
          onClick={handleGoogle}
          aria-busy={pending}
          disabled={pending}
          className="h-11"
        >
          <GoogleIcon className="h-4 w-4" />
          Continuar com Google
        </Button>

        <div className="flex items-center gap-3 text-[13px] text-text-secondary">
          <span className="h-px flex-1 bg-border" />
          ou com email
          <span className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={handleRequest} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="text-xs font-medium text-text-secondary">
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
            required
            autoFocus
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="email"
            className="h-11 text-base"
          />
        </div>

        {errorMsg() && (
          <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 px-3 py-2 text-xs text-accent-rose">
            {errorMsg()}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          aria-busy={pending}
          disabled={pending || !email}
          className="mt-2 h-11"
        >
          {pending ? (
            "Enviando..."
          ) : (
            <>
              <Mail className="h-4 w-4" strokeWidth={2} />
              Enviar código
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </>
          )}
        </Button>
        </form>

        <button
          type="button"
          onClick={() => {
            setStep("password");
            setError(undefined);
          }}
          aria-busy={pending}
          disabled={pending || !email}
          className="flex items-center justify-center gap-1.5 py-1 text-xs text-text-muted transition-colors hover:text-text-secondary disabled:opacity-40"
        >
          <KeyRound className="h-3 w-3" strokeWidth={2} />
          Entrar com senha
        </button>
      </div>
    );
  }

  if (step === "password") {
    return (
      <form onSubmit={handlePassword} className="flex flex-col gap-4">
        <div className="rounded-md border border-accent-cyan/20 bg-accent-cyan/5 px-3 py-2.5 text-center text-xs text-text-secondary">
          Entrando como <strong className="text-text-primary">{email}</strong>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="password" className="text-xs font-medium text-text-secondary">
            Senha
          </label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            autoComplete="current-password"
            className="h-11 text-base"
          />
        </div>

        {errorMsg() && (
          <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 px-3 py-2 text-xs text-accent-rose">
            {errorMsg()}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          aria-busy={pending}
          disabled={pending || !password}
          className="mt-2 h-11"
        >
          {pending ? "Entrando..." : "Entrar"}
        </Button>

        <button
          type="button"
          onClick={() => {
            setStep("email");
            setPassword("");
            setError(undefined);
          }}
          className="flex items-center justify-center gap-1 py-1 text-xs text-text-muted transition-colors hover:text-text-secondary"
        >
          <ArrowLeft className="h-3 w-3" strokeWidth={2} />
          Usar outro email
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleVerify} className="flex flex-col gap-4">
      <div className="rounded-md border border-accent-cyan/20 bg-accent-cyan/5 px-3 py-2.5 text-center text-xs text-text-secondary">
        Código enviado pra <strong className="text-text-primary">{email}</strong>
        <br />
        <span className="text-text-muted">verifica caixa de entrada e spam</span>
      </div>

      <div className="flex flex-col gap-2">
        <label id="code-label" className="text-xs font-medium text-text-secondary">
          Código de 6 dígitos
        </label>
        <CodeInput value={code} onChange={setCode} hasError={Boolean(error)} />
      </div>

      {errorMsg() && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 px-3 py-2 text-xs text-accent-rose">
          {errorMsg()}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        aria-busy={pending}
        disabled={pending || code.length !== 6}
        className="mt-2 h-11"
      >
        {pending ? "Verificando..." : "Entrar"}
      </Button>

      <button
        type="button"
        onClick={() => {
          setStep("email");
          setCode("");
          setError(undefined);
        }}
        className="flex items-center justify-center gap-1 py-1 text-xs text-text-muted transition-colors hover:text-text-secondary"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={2} />
        Usar outro email
      </button>
    </form>
  );
}

function CodeInput({
  value,
  onChange,
  hasError,
}: {
  value: string;
  onChange: (next: string) => void;
  hasError: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] ?? "");

  function setDigitAt(index: number, digit: string) {
    const cleaned = digit.replace(/\D/g, "").slice(0, 1);
    const next = digits.slice();
    next[index] = cleaned;
    onChange(next.join("").slice(0, 6));
  }

  function focusAt(index: number) {
    const el = refs.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  }

  function handleChange(index: number, raw: string) {
    if (raw.length > 1) {
      const cleaned = raw.replace(/\D/g, "").slice(0, 6);
      if (cleaned.length === 0) return;
      onChange(cleaned.padEnd(6, "").slice(0, 6));
      const target = Math.min(cleaned.length, 5);
      requestAnimationFrame(() => focusAt(target));
      return;
    }
    setDigitAt(index, raw);
    if (raw && index < 5) focusAt(index + 1);
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      setDigitAt(index - 1, "");
      focusAt(index - 1);
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    } else if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      focusAt(index + 1);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 0) return;
    e.preventDefault();
    onChange(pasted);
    requestAnimationFrame(() => focusAt(Math.min(pasted.length, 5)));
  }

  return (
    <div role="group" aria-labelledby="code-label" className="flex justify-between gap-2">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.currentTarget.select()}
          autoFocus={i === 0}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Dígito ${i + 1} de 6`}
          className={cn(
            "size-12 sm:size-14 rounded-xl border bg-bg-elevated text-center text-2xl font-semibold text-text-primary outline-hidden transition-colors [font-variant-numeric:tabular-nums]",
            "border-border focus:border-accent-cyan focus:ring-2 focus:ring-accent-cyan/30",
            digit && !hasError && "border-accent-cyan/60 bg-accent-cyan/5",
            hasError && "border-accent-rose/60 focus:border-accent-rose focus:ring-accent-rose/30",
          )}
        />
      ))}
    </div>
  );
}
